import { useMutation, useQuery } from '@tanstack/react-query'
import GlimpsePopup from 'components/popup/popup.component'
import GlimpseProgressDots from 'components/progress-dots/progress-dots.component'
import { IChatWalkthrough } from 'interfaces/user.interface'
import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { getUserStatus, updateUserStatus } from 'services/setting.service'

export const ChatWalkthrough = () => {
  const [popup, setPopup] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)

  const handleUpdateUserStatus = useMutation({
    mutationFn: (data: IChatWalkthrough) => updateUserStatus(data),
    retry: 0,
    onSuccess: async res => {
      setPopup(false)
    },
  })

  useQuery({
    queryKey: ['getUserStatus'],
    queryFn: async () => {
      const response = await getUserStatus()
      if (response.profile.advisor_onboarding_completed) {
        setPopup(false)
      } else {
        setPopup(true)
      }
      return response
    },
  })

  const handleNext = async () => {
    setCurrentSlide(prev => prev + 1)
  }

  const handlePrev = async () => {
    setCurrentSlide(prev => prev - 1)
  }

  const handleCompleWalkThrought = () => {
    const payload = {
      advisor_onboarding_completed: true,
    }
    handleUpdateUserStatus.mutate(payload)
  }

  return (
    <GlimpsePopup
      title={''}
      popup={popup}
      showCross={true}
      onClose={() => {
        handleCompleWalkThrought()
      }}
      customClass="overflow-hidden"
      closeText="Skip"
    >
      <div className="w-full h-max-content">
        <GlimpseProgressDots
          key={'ConditionalDeeperDive'}
          totalSlides={6}
          currentSlide={currentSlide + 1}
          onNext={handleNext}
          onPrev={handlePrev}
          enableQuiz={false}
          enableSorting={false}
          isQuiz={false}
          handleFinish={() => {
            handleCompleWalkThrought()
          }}
          enableNext={true}
          maxHeight={true}
          handleSortNow={() => {}}
        >
          {/* Slide Content */}
          {currentSlide === 0 && (
            <div className="flex flex-col items-center text-center space-y-3">
              <h2 className="text-2xl font-semibold text-brand">
                Welcome to Career Sherpa
              </h2>
              <p className="text-body text-text-muted max-w-md">
                Meet your personal career advisor! Career Sherpa is an
                AI-powered chat agent designed to guide you through career
                decisions, answer your questions, and help you navigate your
                professional journey with confidence.
              </p>
            </div>
          )}

          {currentSlide === 1 && (
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="flex flex-row w-fit gap-2 items-center text-white text-sm font-semibold h-[39px] bg-action border-none p-4 rounded-full">
                <Sparkles size={16} />
                <span>Explore with Career Sherpa</span>
              </div>
              <h2 className="text-2xl font-semibold text-brand">
                How to Start a Conversation
              </h2>
              <p className="text-body text-text-muted max-w-md">
                Simply type your career-related question or concern in the chat
                box. Whether you're exploring new career paths, need advice on
                job interviews, or want guidance on professional development,
                just ask!
              </p>
              <div className="bg-background text-text-heading p-3 rounded-lg text-body">
                <strong>Example:</strong> "I'm considering a career change to
                tech. What should I know?"
              </div>
            </div>
          )}

          {currentSlide === 2 && (
            <div className="flex flex-col items-center text-center space-y-3">
              <img
                src="/assets/images/career-sherpa-conversation.png"
                alt="Chat welcome and follow up"
                className="w-48 h-48 object-contain"
              />
              <h2 className="text-2xl font-semibold text-brand">
                Personalized Guidance
              </h2>
              <p className="text-body text-text-muted max-w-md">
                Career Sherpa will welcome you warmly and ask follow-up
                questions to better understand your situation. This helps
                provide more tailored and relevant advice for your specific
                career goals.
              </p>
              <div className="bg-background text-text-heading p-3 rounded-lg text-body">
                The more details you share, the better Career Sherpa can assist
                you!
              </div>
            </div>
          )}

          {currentSlide === 3 && (
            <div className="flex flex-col items-center text-center space-y-3">
              <img
                src="/assets/images/chat-options.png"
                alt="Chat options"
                className="w-48 h-48 object-contain"
              />
              <h2 className="text-2xl font-semibold text-brand">
                Chat Options
              </h2>
              <p className="text-body text-text-muted max-w-md">
                You have two main options to manage your conversations with
                Career Sherpa:
              </p>
              <div className="flex flex-col space-y-3 w-full max-w-sm">
                <div className="bg-background p-3 rounded-lg text-center">
                  <strong className="text-text-heading">View Chat History</strong>
                  <p className="text-body-sm text-text-muted">
                    Access previous conversations
                  </p>
                </div>
                <div className="bg-background p-3 rounded-lg text-center">
                  <strong className="text-text-heading">New Chat</strong>
                  <p className="text-body-sm text-text-muted">
                    Start a fresh conversation
                  </p>
                </div>
              </div>
            </div>
          )}

          {currentSlide === 4 && (
            <div className="flex flex-col items-center text-center space-y-3">
              <img
                src="/assets/images/chat-session-manager.png"
                alt="Chat history explanation"
                className="w-48 h-48 object-contain"
              />
              <h2 className="text-2xl font-semibold text-brand">
                View Chat History
              </h2>
              <p className="text-body text-text-muted max-w-md">
                Access all your previous conversations with Career Sherpa. This
                feature allows you to:
              </p>
              <ul className="text-left space-y-2 max-w-md">
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-brand rounded-full shrink-0"></span>
                  <span className="text-body text-text-heading">
                    Review past career advice
                  </span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-brand rounded-full shrink-0"></span>
                  <span className="text-body text-text-heading">
                    Continue previous discussions
                  </span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-brand rounded-full shrink-0"></span>
                  <span className="text-body text-text-heading">
                    Track your career journey progress
                  </span>
                </li>
              </ul>
            </div>
          )}

          {currentSlide === 5 && (
            <div className="flex flex-col items-center text-center space-y-3">
              <img
                src="/assets/images/new-chat.png"
                alt="New chat explanation"
                className="w-48 h-48 object-contain"
              />
              <h2 className="text-2xl font-semibold text-brand">
                Start a New Chat
              </h2>
              <p className="text-body text-text-muted max-w-md">
                Begin a fresh conversation when you want to explore a completely
                new topic or prefer a clean slate. Perfect for:
              </p>
              <ul className="text-left space-y-2 max-w-md">
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-brand rounded-full shrink-0"></span>
                  <span className="text-body text-text-heading">
                    Exploring different career paths
                  </span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-brand rounded-full shrink-0"></span>
                  <span className="text-body text-text-heading">
                    Starting a focused discussion
                  </span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-brand rounded-full shrink-0"></span>
                  <span className="text-body text-text-heading">
                    Getting quick, specific answers
                  </span>
                </li>
              </ul>
              <div className="bg-background text-text-heading p-3 rounded-lg text-body mt-4">
                Ready to start your career journey? Let's chat!
              </div>
            </div>
          )}
        </GlimpseProgressDots>
      </div>
    </GlimpsePopup>
  )
}
